import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Header from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

type DeclineReason =
  | "too_far"
  | "not_enough_pay"
  | "too_many_stops"
  | "bad_timing"
  | "not_interested"
  | "other";

const REASON_LABEL: Record<DeclineReason | string, string> = {
  too_far: "Too far",
  not_enough_pay: "Pay too low",
  too_many_stops: "Too many stops",
  bad_timing: "Bad timing",
  not_interested: "Not interested",
  other: "Other",
};

interface DeclineStatsResponse {
  windowDays: number;
  reasons: { reason: string; count: number }[];
  topDecliners: { userId: string; userName: string; count: number }[];
  recent: Array<{
    id: string;
    createdAt: string | null;
    reason: string;
    note: string | null;
    userId: string;
    userName: string;
    deliveryRequestId: string;
    estimatedMiles: number | null;
    driverPay: number | null;
  }>;
}

function formatWhen(value: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}

function ReasonBar({ label, count, total }: { label: string; count: number; total: number }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="w-32 text-muted-foreground">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-primary/80" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-8 text-right tabular-nums text-muted-foreground">{count}</span>
    </div>
  );
}

export default function AdminDeliveryDeclines() {
  const [windowDays, setWindowDays] = useState(7);

  const { data, isLoading, isError, error } = useQuery<DeclineStatsResponse>({
    queryKey: ["/api/admin/delivery-declines", windowDays],
    queryFn: async () => {
      const res = await fetch(`/api/admin/delivery-declines?windowDays=${windowDays}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const totalInWindow = data?.reasons.reduce((sum, r) => sum + r.count, 0) ?? 0;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-4 py-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Delivery declines</h1>
          <p className="text-sm text-muted-foreground">
            Why drivers pass on offers. Use this to spot offers that need better pay or shorter
            routes.
          </p>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Window:</span>
          {[7, 14, 30, 90].map((d) => (
            <Button
              key={d}
              type="button"
              variant={windowDays === d ? "default" : "outline"}
              size="sm"
              onClick={() => setWindowDays(d)}
            >
              {d}d
            </Button>
          ))}
        </div>

        {isLoading ? (
          <Card>
            <CardContent className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </CardContent>
          </Card>
        ) : isError ? (
          <Card>
            <CardContent className="py-8 text-sm text-destructive">
              Failed to load: {(error as Error)?.message || "unknown error"}
            </CardContent>
          </Card>
        ) : data ? (
          <>
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Top reasons · last {data.windowDays}d ({totalInWindow} declines)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {data.reasons.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No declines in this window.</p>
                  ) : (
                    data.reasons.map((r) => (
                      <ReasonBar
                        key={r.reason}
                        label={REASON_LABEL[r.reason] ?? r.reason}
                        count={r.count}
                        total={totalInWindow}
                      />
                    ))
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Top decliners · last {data.windowDays}d</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {data.topDecliners.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No declines in this window.</p>
                  ) : (
                    data.topDecliners.map((d) => (
                      <div
                        key={d.userId}
                        className="flex items-center justify-between gap-2 text-sm"
                      >
                        <span>{d.userName}</span>
                        <span className="tabular-nums text-muted-foreground">{d.count}</span>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Recent declines</CardTitle>
              </CardHeader>
              <CardContent className="divide-y">
                {data.recent.length === 0 ? (
                  <p className="py-2 text-sm text-muted-foreground">None.</p>
                ) : (
                  data.recent.map((row) => (
                    <div key={row.id} className="py-3 text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{row.userName}</span>
                        <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
                          {REASON_LABEL[row.reason] ?? row.reason}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatWhen(row.createdAt)}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        Request {row.deliveryRequestId.slice(0, 8)} ·{" "}
                        {row.estimatedMiles != null ? `${row.estimatedMiles} mi · ` : ""}
                        {row.driverPay != null ? `$${row.driverPay.toFixed(2)} pay` : ""}
                      </div>
                      {row.note && (
                        <p className="mt-2 text-sm text-foreground/90">"{row.note}"</p>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </>
        ) : null}
      </main>
    </div>
  );
}
